/**
 * message-history-api — wires Messaging → Delivery to the typed notification
 * history ops (wiring kit `…-notification-history-9bf1703c`):
 *
 *   listMessageHistory       GET /v1/admin/message-history       (table)
 *   getMessageHistoryDetail  GET /v1/admin/message-history/{id}   (drawer)
 *
 * Every call goes through the typed @v0y4ge/api-client (never a hand-built
 * fetch). Results are `tag`-reported for the data-source transparency strip.
 *
 * **Casing (CC 2026-09-03 answers).** These endpoints are camelCase —
 * `contentSource`, `providerMessageId`, `sent`, `events[].at`. This file used to
 * read snake_case for every field, so `contentSource` and `providerMessageId`
 * would have read `undefined` and the chip and provider id would never have
 * appeared. Names are camelCase-first now, with the snake_case spelling kept as
 * a fallback per field: the two shapes are indistinguishable to a reader, the
 * preview seed layer and the deployed backend have disagreed on casing before,
 * and a field that silently reads `undefined` is exactly the failure this note
 * is about. `pick` is what makes that cheap.
 *
 * Adapters map the contract shapes onto the DeliveryRow the table + drawer
 * render, so the UI is unchanged — only the data source moved.
 */
import { listMessageHistory, getMessageHistoryDetail } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";
import type { DeliveryRow, DelivEvent } from "./delivery-data";

/** First value that is neither undefined nor null nor "". */
const pick = (...vals: unknown[]): string | undefined => {
  const v = vals.find((x) => x !== undefined && x !== null && x !== "");
  return v == null ? undefined : String(v);
};

/* Contract shapes (mirrors @v0y4ge/api-contracts; kept local so the screen
 * doesn't depend on contract type resolution — types are erased at build).
 * Declared loosely on purpose: camelCase is the wire, snake_case is tolerated. */
export interface MHItem {
  id: string; channel: string; status: string; provider: string;
  displayId?: string; display_id?: string;
  credentialSource?: string; credential_source?: string;
  subject?: string | null;
  toAddress?: string; to_address?: string;
  toName?: string | null; to_name?: string | null;
  fromName?: string | null; from_name?: string | null;
  fromAddress?: string; from_address?: string;
  sourceKind?: string | null; source_kind?: string | null;
  sourceLabel?: string | null; source_label?: string | null;
  /** The send's instant. `sent` is the wire's name; `createdAt` is tolerated. */
  sent?: string; createdAt?: string; created_at?: string;
  lastEventAt?: string | null; last_event_at?: string | null;
  /** `dispatch` = the content the customer received, stored at hand-off;
   *  `template` = a send made before rendered content was stored, so the
   *  template is returned and its tokens are unresolved by design. */
  contentSource?: "dispatch" | "template" | null;
  /** The provider's own id for the send — evidence for a support ticket. */
  providerMessageId?: string | null;
  /** True when the render left one or more tokens unresolved. Read but not yet
   *  drawn — see the ask in CD-REPLY-messaging-library-answers-2026-09-03 §4. */
  hasMissingTokens?: boolean;
  /** JAI-1377: a queued row's planned instant. Absent until that ships, and
   *  NOT substituted from `sent`/`createdAt` — stating a time that is not the
   *  plan is worse than stating none. */
  scheduledFor?: string | null;
}
export interface MHEvent {
  type: string;
  at?: string; occurredAt?: string; occurred_at?: string;
  meta?: string | null;
  clientLabel?: string | null; client_label?: string | null;
  bounceClassification?: string | null; bounce_classification?: string | null;
  bounceReason?: string | null; bounce_reason?: string | null;
  missingTokens?: string[];
}
export interface MHDetail extends MHItem {
  replyTo?: string | null; reply_to?: string | null;
  events: MHEvent[];
  renderedHtml?: string | null; rendered_html?: string | null;
  renderedText?: string | null; rendered_text?: string | null;
  bodyUrl?: string | null; body_url?: string | null;
}
export interface MHListResponse { data: MHItem[]; total: number; page: number; limit: number; hasMore: boolean; }
export interface MHListParams {
  channel?: string; status?: string; recipient?: string; source_kind?: string;
  from_date?: string; to_date?: string; page?: number; limit?: number;
}

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export function fetchMessageHistory(params: MHListParams = {}): Promise<MHListResponse> {
  return tag("listMessageHistory", listMessageHistory(params as never) as Promise<MHListResponse>);
}
/** The delivery TOTAL, for the rail badge. One row is fetched and the count is
 *  read off the envelope's `total` — never `data.length`, which is the size of
 *  the page `limit` asked for and therefore a floor on the real number wearing
 *  the answer's clothes. Restored 2026-09-09 (CD docket §2). */
export function fetchDeliveryTotal(): Promise<number> {
  return fetchMessageHistory({ limit: 1 }).then((r) => r.total);
}
export function fetchMessageHistoryDetail(id: string): Promise<MHDetail> {
  return tag("getMessageHistoryDetail", getMessageHistoryDetail(id) as Promise<MHDetail>);
}

/* ── Shape mapping ─────────────────────────────────────────────────────── */
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** Format an ISO instant as "13 Jul, 10:00:04" (UTC) — matches delivTimeVal's
 *  parser so sort works. A value that is not an instant is passed THROUGH
 *  rather than blanked: `sent` may already arrive display-shaped, and dropping
 *  it would empty the column with no way to tell why. */
function fmtAt(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}, ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
}

/** Contract sourceKind → the delivery log's source vocabulary. */
function mapSourceKind(kind?: string | null): string {
  switch (kind) {
    case "journey": return "flow";
    case "trigger": return "trigger";
    case "campaign": return "campaign";
    case "manual": return "manual";
    default: return "manual";
  }
}

const sentAt = (i: MHItem): string => fmtAt(pick(i.sent, i.createdAt, i.created_at));
const lastAt = (i: MHItem): string => fmtAt(pick(i.lastEventAt, i.last_event_at, i.sent, i.createdAt, i.created_at));
const toAddr = (i: MHItem): string => pick(i.toAddress, i.to_address) ?? "";

function sender(item: MHItem): { name: string; from: string } {
  return { name: pick(item.fromName, item.from_name) ?? "System", from: pick(item.fromAddress, item.from_address) ?? "" };
}
function recipient(item: MHItem): { name: string; to: string } {
  return { name: pick(item.toName, item.to_name) ?? toAddr(item), to: toAddr(item) };
}
function source(item: MHItem): { kind: string; label: string } {
  return {
    kind: mapSourceKind(pick(item.sourceKind, item.source_kind)),
    label: pick(item.sourceLabel, item.source_label) ?? "System notification",
  };
}

/** Provenance fields shared by the list row and the drawer row. */
function provenance(i: MHItem) {
  return {
    ...(i.contentSource ? { contentSource: i.contentSource } : {}),
    ...(i.providerMessageId ? { providerMessageId: i.providerMessageId } : {}),
    ...(i.hasMissingTokens ? { hasMissingTokens: true } : {}),
    /* Only from `scheduledFor`, and only on a queued row. No fallback: with
       JAI-1377 not shipped, a queued row shows "Queued" and no time. */
    ...(i.status === "queued" && i.scheduledFor ? { plannedAt: fmtAt(i.scheduledFor) } : {}),
  };
}

/** A list row — no timeline/body yet (that's the detail fetch on open). */
export function itemToRow(item: MHItem): DeliveryRow {
  const isEmail = item.channel === "email";
  return {
    ...provenance(item),
    id: item.id,
    channel: item.channel,
    sender: sender(item),
    recipient: recipient(item),
    source: source(item),
    subject: isEmail ? (item.subject ?? undefined) : undefined,
    text: isEmail ? undefined : (pick(item.sourceLabel, item.source_label) ?? "SMS notification"),
    sent: sentAt(item),
    // Single projected-status event drives the row's pill + "latest event" sort.
    events: [{ type: item.status, at: lastAt(item) }],
    displayId: pick(item.displayId, item.display_id),
    status: item.status,
    provider: item.provider,
    credentialSource: pick(item.credentialSource, item.credential_source),
  };
}

/** The full drawer row — real event timeline + rendered body. */
export function detailToRow(d: MHDetail): DeliveryRow {
  const isEmail = d.channel === "email";
  const events: DelivEvent[] = (d.events || []).map((e) => {
    const meta = pick(e.meta, e.bounceReason, e.bounce_reason, e.clientLabel, e.client_label);
    const at = fmtAt(pick(e.at, e.occurredAt, e.occurred_at));
    const mt = e.missingTokens && e.missingTokens.length ? { missingTokens: e.missingTokens } : {};
    return meta ? { type: e.type, at, meta, ...mt } : { type: e.type, at, ...mt };
  });
  // Keep the projected-status pill available even when the timeline is empty.
  if (events.length === 0) events.push({ type: d.status, at: lastAt(d) });
  const html = pick(d.renderedHtml, d.rendered_html);
  const text = pick(d.renderedText, d.rendered_text);
  return {
    ...provenance(d),
    id: d.id,
    channel: d.channel,
    sender: sender(d),
    recipient: recipient(d),
    source: source(d),
    subject: d.subject ?? undefined,
    text,
    html,
    sent: sentAt(d),
    events,
    displayId: pick(d.displayId, d.display_id),
    status: d.status,
    provider: d.provider,
    credentialSource: pick(d.credentialSource, d.credential_source),
    replyTo: pick(d.replyTo, d.reply_to),
    bodyUnavailable: isEmail ? !html : !text,
  };
}
